import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
};

// Format number with exact value (no K/M abbreviation)
export const formatNumberExact = (num) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

// Supported currencies with their configurations
export const SUPPORTED_CURRENCIES = {
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US' },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE' },
  GBP: { symbol: '£', name: 'British Pound', locale: 'en-GB' },
  INR: { symbol: '₹', name: 'Indian Rupee', locale: 'en-IN' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', locale: 'en-CA' },
  JPY: { symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', locale: 'zh-CN' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE' },
  BRL: { symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR' },
  MXN: { symbol: '$', name: 'Mexican Peso', locale: 'es-MX' },
  KRW: { symbol: '₩', name: 'South Korean Won', locale: 'ko-KR' },
  THB: { symbol: '฿', name: 'Thai Baht', locale: 'th-TH' },
  IDR: { symbol: 'Rp', name: 'Indonesian Rupiah', locale: 'id-ID' },
  PHP: { symbol: '₱', name: 'Philippine Peso', locale: 'en-PH' },
  VND: { symbol: '₫', name: 'Vietnamese Dong', locale: 'vi-VN' },
  ZAR: { symbol: 'R', name: 'South African Rand', locale: 'en-ZA' },
  NGN: { symbol: '₦', name: 'Nigerian Naira', locale: 'en-NG' },
  EGP: { symbol: 'E£', name: 'Egyptian Pound', locale: 'ar-EG' },
};

// Get currency list for dropdowns
export const getCurrencyList = () => {
  return Object.entries(SUPPORTED_CURRENCIES).map(([code, info]) => ({
    code,
    ...info
  }));
};

// Format currency with specified currency code
export const formatCurrency = (num, currencyCode = 'USD') => {
  const currency = SUPPORTED_CURRENCIES[currencyCode] || SUPPORTED_CURRENCIES.USD;
  
  try {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  } catch (e) {
    // Fallback for unsupported locales
    return `${currency.symbol}${num.toLocaleString()}`;
  }
};

// Get currency symbol
export const getCurrencySymbol = (currencyCode = 'USD') => {
  return SUPPORTED_CURRENCIES[currencyCode]?.symbol || '$';
};

export const formatPercentage = (num) => {
  return num.toFixed(2) + '%';
};

export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const getPlatformColor = (platform) => {
  const colors = {
    'Meta': '#1877F2',
    'LinkedIn': '#0A66C2',
    'TikTok': '#000000',
    'Twitter': '#1DA1F2',
    'DV360': '#4285F4',
    'Galksion': '#6366F1',
    'Mondiads': '#8B5CF6',
    'Propeller': '#F59E0B',
    'Clickadu': '#10B981',
  };
  return colors[platform] || '#64748B';
};

export const getStatusColor = (status) => {
  const colors = {
    'active': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'paused': 'bg-amber-50 text-amber-700 border-amber-200',
    'completed': 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return colors[status] || 'bg-slate-50 text-slate-700 border-slate-200';
};

export const getRoleLabel = (role) => {
  const labels = {
    'super_admin': 'Super Admin',
    'admin': 'Admin',
    'user': 'User',
  };
  return labels[role] || role;
};

export const getRoleColor = (role) => {
  const colors = {
    'super_admin': 'bg-purple-50 text-purple-700 border-purple-200',
    'admin': 'bg-blue-50 text-blue-700 border-blue-200',
    'user': 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return colors[role] || 'bg-slate-50 text-slate-700 border-slate-200';
};
